import React, { useState } from 'react';
import {
  Box,
  Flex,
  Text,
  IconButton,
  useColorModeValue,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Spinner,
  Button,
} from '@chakra-ui/react';
import { AddIcon, ChevronLeftIcon } from '@chakra-ui/icons';
import ReusableTable from 'components/ReusableTable';
import { getUser } from 'constants/helper/authStorage';
import { usePatientHistoryList } from './hooks/usePatientHistoryList';
import { usePatientAllListByObjId } from './hooks/usePatientAllListByObjId';
import formatDateTime from 'constants/helper/formatDateTime';
import ModernStepper from '../patientHistoryManagement/components/ModernStepper';
import TreatmentDetailsCard from '../patientHistoryManagement/components/TreatmentDetailsCard';
import { AnimatePresence } from 'framer-motion';
import useDebounce from 'constants/helper/useDebounce';

const PatientsTableView = ({ doctor, onBack }) => {
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);

  const [dateFilterValues, setDateFilterValues] = useState({
    year: '',
    month: '',
    fromDate: '',
    toDate: '',
  });

  const [page, setPage] = useState(1);
  const [searchString, setSearchString] = useState('');
  const debouncedSearch = useDebounce(searchString, 500);
  const STEPS_PER_PAGE = 11;

  const [stepPage, setStepPage] = useState(0);
  const [historyPage, setHistoryPage] = useState(1); // NEW: Pagination for history

  const { patientHistory, totalPages, isLoading } = usePatientHistoryList(
    page,
    debouncedSearch,
    doctor?._id,
    dateFilterValues,
  );

  const {
    patientHistory: patientDetails = [],
    totalPages: historyTotalPages,
    isLoading: isLoadingPatientHistory,
  } = usePatientAllListByObjId(historyPage, selectedPatient); // Use historyPage

  const [activeVisitIndex, setActiveVisitIndex] = useState(0);
  const visits = patientDetails || [];

  const activeVisit = visits[activeVisitIndex];

  const handleSearch = (value) => {
    setSearchString(value);
  };

  const handleDateFilterChange = (values) => {
    setDateFilterValues(values);
    setPage(1);
  };

  const tableData = patientHistory?.map((item, index) => ({
    ...item,
    srNo: (page - 1) * 20 + index + 1,
  }));

  const userDetails = getUser();

  const formattedData = tableData?.map((item) => {
    const doctorName = userDetails?.name;
    const diseases = item.diseases || [];

    const engList = diseases.map((d) => d.diseaseEnglish).filter(Boolean);
    const hindList = diseases.map((d) => d.diseaseHindi).filter(Boolean);

    const bottle1Values = [
      item?.bottle1?.E,
      item?.bottle1?.R1,
      item?.bottle1?.R2,
    ].filter((v) => v !== undefined && v !== null && v !== '');

    const bottle2Values = [
      item?.bottle2?.E,
      item?.bottle2?.R1,
      item?.bottle2?.R2,
    ].filter((v) => v !== undefined && v !== null && v !== '');
    const bottle3Values = [
      item?.bottle3?.E,
      item?.bottle3?.R1,
      item?.bottle3?.R2,
    ].filter((v) => v !== undefined && v !== null && v !== '');
    const bottle4Values = [
      item?.bottle4?.E,
      item?.bottle4?.R1,
      item?.bottle4?.R2,
    ].filter((v) => v !== undefined && v !== null && v !== '');

    return {
      ...item,
      patientId: item?.patientObjId?.patientId,
      doctorName: doctorName,
      patientName: item?.patientObjId?.name,
      patientMobile: item?.patientObjId?.mobile,

      bottle1:
        bottle1Values.length > 1
          ? bottle1Values.join(' + ')
          : bottle1Values[0] || '- - -',
      bottle2:
        bottle2Values.length > 1
          ? bottle2Values.join(' + ')
          : bottle2Values[0] || '- - -',
      bottle3:
        bottle3Values.length > 1
          ? bottle3Values.join(' + ')
          : bottle3Values[0] || '- - -',
      bottle4:
        bottle4Values.length > 1
          ? bottle4Values.join(' + ')
          : bottle4Values[0] || '- - -',

      diseaseEng: engList.length > 1 ? engList.join(' / ') : engList[0] || '',

      diseaseHind:
        hindList.length > 1 ? hindList.join(' / ') : hindList[0] || '',
    };
  });

  const handleView = (row) => {
    setSelectedPatient(row?.patientObjId?._id);
    setHistoryPage(1);
    setActiveVisitIndex(0);
    setStepPage(0);
    setIsViewOpen(true);
  };

  const handleClose = () => {
    setIsViewOpen(false);
    setSelectedPatient(null);
  };

  const modalBg = useColorModeValue('white', 'gray.800');
  const modalBorder = useColorModeValue('gray.200', 'gray.700');
  const headerBg = useColorModeValue('blue.600', 'blue.700');
  const textStrong = useColorModeValue('gray.700', 'gray.200');
  const spinnerEmpty = useColorModeValue('gray.200', 'gray.600');

  return (
    <Box>
      <Flex align="center" gap={3} mb={4}>
        <IconButton
          icon={<ChevronLeftIcon />}
          size="sm"
          onClick={onBack}
          aria-label="Back"
        />
        <Text fontWeight="bold">Patients of {doctor?.name}</Text>
      </Flex>

      <ReusableTable
        data={formattedData}
        columns={[
          { key: 'srNo', label: 'Sr.No', width: '80px', headerSize: '14px' },

          {
            key: 'patientId',
            label: 'Patient ID',
            width: '300px',
            headerSize: '14px',
          },
          {
            key: 'patientName',
            label: 'Patient Name',
            width: '350px',
            headerSize: '14px',
          },
          {
            key: 'patientMobile',
            label: 'Mobile',
            width: '300px',
            headerSize: '14px',
          },
          {
            key: 'diseaseEng',
            label: 'Disease Name Eng',
            width: '350px',
            headerSize: '14px',
          },
          {
            key: 'diseaseHind',
            label: 'Disease Name Hin',
            width: '350px',
            headerSize: '14px',
          },
          {
            key: 'action',
            label: 'Action',
            width: '200px',
            render: (_, row) => (
              <Flex gap={2}>
                <Button
                  size="sm"
                  colorScheme="blue"
                  onClick={() => handleView(row)}
                >
                  History
                </Button>
              </Flex>
            ),
          },
        ]}
        page={page}
        totalPages={totalPages || 1}
        loading={isLoading}
        onPageChange={(newPage) => setPage(newPage)}
        showTopAction={true}
        showAddBtn={false}
        topAction={{
          title: '',
          label: 'Add Protocol',
          icon: <AddIcon />,
        }}
        showViewToggle={false}
        showSearch={true}
        onSearch={handleSearch}
        showDateFilter={true}
        dateFilterValues={dateFilterValues}
        onDateFilterChange={handleDateFilterChange}
      />

      <Modal
        isOpen={isViewOpen}
        onClose={handleClose}
        size={{ base: 'full', md: 'xl', xl: '6xl' }}
        isCentered
        scrollBehavior="inside"
      >
        <ModalOverlay bg="blackAlpha.700" />

        <ModalContent
          borderRadius="xl"
          overflow="hidden"
          bg={modalBg}
          borderWidth="1px"
          borderColor={modalBorder}
        >
          <ModalHeader
            bg={headerBg}
            color="white"
            fontSize="lg"
            fontWeight="bold"
          >
            Patient Treatment Details
          </ModalHeader>

          <ModalCloseButton color="white" />

          <ModalBody p={6} maxH="80vh" overflowY="auto">
            <Box>
              {visits.length > 0 && (
                <Box mb={8}>
                  <ModernStepper
                    steps={visits.map((v, i) => ({
                      label: v.visitDate || `Visit`,
                    }))}
                    activeStep={activeVisitIndex}
                    setActiveStep={setActiveVisitIndex}
                    stepPage={stepPage}
                    setStepPage={setStepPage}
                    stepsPerPage={STEPS_PER_PAGE}
                    hasNextPage={historyPage < (historyTotalPages || 1)}
                    hasPrevPage={historyPage > 1}
                    onNextPage={() => {
                      setHistoryPage((p) => p + 1);
                      setStepPage(0);
                      setActiveVisitIndex(0);
                    }}
                    onPrevPage={() => {
                      setHistoryPage((p) => p - 1);
                      setStepPage(0);
                      setActiveVisitIndex(0);
                    }}
                    startIndex={(historyPage - 1) * 10} // Fix numbering
                  />
                </Box>
              )}

              {isLoadingPatientHistory ? (
                <Flex
                  direction="column"
                  align="center"
                  justify="center"
                  h="400px"
                >
                  <Spinner
                    thickness="4px"
                    speed="0.65s"
                    emptyColor={spinnerEmpty}
                    color="blue.500"
                    size="xl"
                  />
                  <Text mt={4} fontWeight="semibold" color={textStrong}>
                    Loading Records...
                  </Text>
                </Flex>
              ) : (
                <AnimatePresence mode="wait">
                  {activeVisit && (
                    <TreatmentDetailsCard
                      key={activeVisit._id}
                      visit={activeVisit}
                    />
                  )}
                </AnimatePresence>
              )}
            </Box>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default PatientsTableView;
